import { RCALibraryEntity } from "entities/RCA/RCALibraryEntity/RCALibraryEntity";
import { useCallback, useEffect, useMemo, useState } from "react";
import { IPositionedCategory, IUseFishboneDiagram } from "./RCAFishboneDiagramInterfaces";
import {
    IFishboneDiagram,
    IFishboneDiagramProblemCause,
} from "entities/RCA/RCALibraryEntity/RCALibraryEntityInterfaces";
import { FIXED_MAIN_HORIZONTAL_LINE_HEIGHT } from "constants/constants";

const useFishboneDiagram = ({
    onCreateOrEditFishboneDiagram,
    fishboneDiagram,
    fishboneCategories,
    isLoadingFishboneCategories,
    mainHorizontalLineWrapperRef,
    verticalUpArrowsRefs,
    verticalDownArrowsRefs,
    mainDiagramWrapperRef,
}: IUseFishboneDiagram) => {
    const { upsertFishboneDiagramItem, loading: isSavingFishboneDiagram } = RCALibraryEntity.UpsertFishboneDiagram();
    const { upsertFishboneDiagramProblemCauseItem, loading: isSavingFishboneDiagramProblemCause } =
        RCALibraryEntity.UpsertFishboneDiagramProblemCause();
    const [topCategories, setTopCategories] = useState<IPositionedCategory[]>([]);
    const [bottomCategories, setBottomCategories] = useState<IPositionedCategory[]>([]);
    const [minimalVerticalLineHeightTop, setMinimalVerticalLineHeightTop] = useState<number>(0);
    const [fishboneDiagramData, setFishboneDiagramData] = useState<Partial<IFishboneDiagram>>(fishboneDiagram || {});
    const [fishboneDiagramProblemCausesData, setFishboneDiagramProblemCausesData] = useState<
        IFishboneDiagramProblemCause[]
    >(fishboneDiagram?.fishbone_diagram_problem_causes || []);

    useEffect(() => {
        setFishboneDiagramData(fishboneDiagram || {});
        setFishboneDiagramProblemCausesData(fishboneDiagram?.fishbone_diagram_problem_causes || []);
    }, [fishboneDiagram]);

    useEffect(() => {
        const calculateCategoryPositions = () => {
            if (!fishboneCategories || isLoadingFishboneCategories) return;

            const fishboneDiagramCategoryDivision = Math.round(fishboneCategories.length / 2);
            const categoryWidth = mainHorizontalLineWrapperRef.current?.offsetWidth;
            if (categoryWidth) {
                const margin = (categoryWidth - 100) / (fishboneDiagramCategoryDivision + 1);
                const distance = (categoryWidth - 2 * margin) / (fishboneDiagramCategoryDivision - 1);
                const positions: number[] = [];
                for (let i = 0; i < fishboneDiagramCategoryDivision; i++) {
                    positions.push(margin + distance * i);
                }
                let numberOfCategoriesCreated = 1;
                const currentTopCategories: IPositionedCategory[] = [];
                const currentBottomCategories: IPositionedCategory[] = [];
                fishboneCategories.forEach((category) => {
                    if (numberOfCategoriesCreated <= fishboneDiagramCategoryDivision) {
                        currentTopCategories.push({
                            ...category,
                            position: positions[numberOfCategoriesCreated - 1],
                        });
                    } else {
                        currentBottomCategories.push({
                            ...category,
                            position: positions[numberOfCategoriesCreated - 1 - fishboneDiagramCategoryDivision],
                        });
                    }
                    numberOfCategoriesCreated++;
                });
                setTopCategories(currentTopCategories);
                setBottomCategories(currentBottomCategories);
            }
        };
        calculateCategoryPositions();
        window.addEventListener("resize", calculateCategoryPositions);
        return () => {
            window.removeEventListener("resize", calculateCategoryPositions);
        };
    }, [fishboneCategories, isLoadingFishboneCategories, mainHorizontalLineWrapperRef]);

    const calculateFisboneHeight = useCallback(() => {
        let theMostUpVerticalLineHeight = 0;
        let theMostDownVerticalLineHeight = 0;

        verticalUpArrowsRefs.current.forEach((ref) => {
            if (ref && ref.offsetHeight > theMostUpVerticalLineHeight) {
                theMostUpVerticalLineHeight = ref.offsetHeight;
            }
        });

        verticalDownArrowsRefs.current.forEach((ref) => {
            if (ref && ref.offsetHeight > theMostDownVerticalLineHeight) {
                theMostDownVerticalLineHeight = ref.offsetHeight;
            }
        });

        if (mainDiagramWrapperRef.current) {
            mainDiagramWrapperRef.current.style.height = `${
                theMostUpVerticalLineHeight + theMostDownVerticalLineHeight + FIXED_MAIN_HORIZONTAL_LINE_HEIGHT
            }px`;
        }
        setMinimalVerticalLineHeightTop(theMostUpVerticalLineHeight);
    }, [mainDiagramWrapperRef, verticalDownArrowsRefs, verticalUpArrowsRefs]);

    useEffect(() => {
        calculateFisboneHeight();
        window.addEventListener("resize", calculateFisboneHeight);
        return () => {
            window.removeEventListener("resize", calculateFisboneHeight);
        };
    }, [topCategories, bottomCategories, fishboneDiagramProblemCausesData, calculateFisboneHeight]);

    const handleOnRemoveItem = async (item: IFishboneDiagramProblemCause) => {
        await upsertFishboneDiagramProblemCauseItem({
            variables: {
                fishbone_diagram_problem_cause: {
                    fishbone_diagram_problem_cause_id: item.fishbone_diagram_problem_cause_id,
                    deleted_at: new Date(),
                    fishbone_diagram_id: item.fishbone_diagram_id,
                    fishbone_diagram_category_id: item.fishbone_diagram_category.fishbone_diagram_category_id,
                    description: item.description,
                },
            },
        });
        await onCreateOrEditFishboneDiagram();
    };

    const createOrEditFishboneDiagram = async ({
        rcaId,
        problemStatement,
        fishboneDiagramId,
    }: {
        rcaId: number | string;
        problemStatement: string;
        fishboneDiagramId?: number;
    }) => {
        const upsertFishboneDiagramResponse = await upsertFishboneDiagramItem({
            variables: {
                fishbone_diagram: {
                    ...(fishboneDiagramId && { fishbone_diagram_id: fishboneDiagramId }),
                    rca_id: rcaId,
                    problem_statement: problemStatement || "",
                },
            },
        });
        await onCreateOrEditFishboneDiagram();
        return upsertFishboneDiagramResponse.data?.insert_fishbone_diagram?.returning[0].fishbone_diagram_id;
    };

    const createOrEditFishboneDiagramProblemCause = async ({
        rcaId,
        fishboneDiagramId,
        descriptionText,
        primaryProblemCauseId,
        fishboneDiagramCategoryId,
        fishboneDiagramProblemCauseId,
        isHighlighted,
    }: {
        rcaId: number | string;
        fishboneDiagramId?: number;
        descriptionText: string;
        primaryProblemCauseId?: number | null;
        fishboneDiagramCategoryId: number;
        fishboneDiagramProblemCauseId?: number;
        isHighlighted: boolean;
    }) => {
        let currentFishboneDiagramId = fishboneDiagramId;

        if (!fishboneDiagramId) {
            const newFishboneDiagramId = await createOrEditFishboneDiagram({
                rcaId,
                problemStatement: "",
            });

            currentFishboneDiagramId = newFishboneDiagramId;
        }

        const upsertFishboneDiagramProblemCauseResponse = await upsertFishboneDiagramProblemCauseItem({
            variables: {
                fishbone_diagram_problem_cause: {
                    ...(fishboneDiagramProblemCauseId && {
                        fishbone_diagram_problem_cause_id: fishboneDiagramProblemCauseId,
                    }),
                    fishbone_diagram_id: currentFishboneDiagramId,
                    description: descriptionText,
                    fishbone_diagram_category_id: fishboneDiagramCategoryId,
                    is_highlighted: isHighlighted,
                    ...(primaryProblemCauseId && { primary_problem_cause_id: primaryProblemCauseId }),
                },
            },
        });
        await onCreateOrEditFishboneDiagram();
        return upsertFishboneDiagramProblemCauseResponse.data?.insert_fishbone_diagram_problem_cause?.returning[0]
            .problem_cause_id;
    };

    const isProcessing = useMemo(
        () => isSavingFishboneDiagram || isSavingFishboneDiagramProblemCause,
        [isSavingFishboneDiagram, isSavingFishboneDiagramProblemCause],
    );

    return {
        createOrEditFishboneDiagram,
        createOrEditFishboneDiagramProblemCause,
        isProcessing,
        minimalVerticalLineHeightTop,
        fishboneDiagramData,
        handleOnRemoveItem,
        topCategories,
        bottomCategories,
        calculateFisboneHeight,
        fishboneDiagramProblemCausesData,
    };
};

export default useFishboneDiagram;
